import { useQueryClient } from "@tanstack/react-query"
import { mentionHandle, type UserSummaryDTO } from "@zplanem/responses"
import {
	type FormEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useActiveChat } from "../../app/active-chat-context"
import { useAcknowledgeChat } from "../../app/hooks/use-acknowledge-chat"
import { useAutoAckOnArrival } from "../../app/hooks/use-auto-ack-on-arrival"
import { useBottomVisibleTrigger } from "../../app/hooks/use-bottom-visible-trigger"
import { useChat } from "../../app/hooks/use-chat"
import {
	setMareczekThinking,
	useMareczekThinking,
} from "../../app/hooks/use-mareczek-thinking"
import { usePropertyMemberships } from "../../app/hooks/use-property-memberships"
import { useTopVisibleTrigger } from "../../app/hooks/use-top-visible-trigger"
import { getTranslations } from "../../app/i18n"
import { useLocale } from "../../app/locale-context"
import { useUser } from "../../app/user-context"
import { parseStatusMarker } from "../../lib/chat-status"
import { getOwnedOrigins } from "../../lib/owned-origins.runtime"
import { renderChatMessage } from "../../lib/render-chat-message"
import type { ChatRole } from "../../lib/role-colors"
import {
	formatFullDateTime,
	formatShortTime,
	getDisplayName,
} from "../../lib/user-display"
import { cn } from "../../lib/utils"
import { AgentActionCard } from "./agent-action-card"
import { AuthorAvatar } from "./author-avatar"
import {
	CHAT_COMPOSER_FIELD_CLASS,
	CHAT_SCROLL_AREA_CLASS,
	ChatSendButton,
	chatBubbleClass,
} from "./chat-bubble"
import { MentionPicker } from "./mention-picker"
import { MentionText } from "./mention-text"
import { MessageMarkdown } from "./message-markdown"
import { resolveSharedChatAuthorTitle } from "./shared-chat-author"
import { applyChatRailInert } from "./shared-chat-dom"
import { ThinkingBubble } from "./thinking-bubble"
import { type MentionCandidate, useMentionCompose } from "./use-mention-compose"

interface Props {
	propertyId: string
	chatId: string | undefined
	projectId?: string
	/**
	 * Project members for a project chat (the @-mention scope). Omitted for a
	 * property chat, where the scope is the property memberships loaded here.
	 */
	members?: UserSummaryDTO[]
	isOpen: boolean
	onClose: () => void
}

function CloseIcon() {
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
		>
			<path d="M18 6 6 18" />
			<path d="m6 6 12 12" />
		</svg>
	)
}

/**
 * Members chat rendered as a sticky right rail on `md+` and as a
 * full-screen overlay on smaller viewports. Visibility is owned by the
 * page (`PropertyDetailPage`) so the toggle button in the `PageActions`
 * cluster (in-page header on md+, mobile top bar below) and the rail
 * itself share one source of truth, persisted per property in
 * `localStorage`.
 *
 * The auto-ack on new arrivals is gated on `isOpen` (replaces the older
 * IntersectionObserver — the closed rail is, by definition, not visible).
 */
export function SharedChat({
	propertyId,
	chatId,
	projectId,
	members,
	isOpen,
	onClose,
}: Props) {
	const { locale } = useLocale()
	const tAll = getTranslations(locale)
	const t = tAll.chat.shared
	const { profile } = useUser()
	const myUserId = profile?.id ?? null
	const railRef = useRef<HTMLElement | null>(null)
	// Read once here (real Vite at the chat container); MessageMarkdown stays
	// import.meta-free by receiving owned origins as a prop. Mareczek's prose
	// replies render as markdown so #139 references become in-app links.
	const ownedOrigins = useMemo(getOwnedOrigins, [])

	// Author lookup map (owner + all members) keyed by user id. Each entry
	// carries the chat-role label so the avatar can color its border and
	// the hover tooltip can read "<display name> (<role>)". Loaded once via
	// TanStack Query; invalidated via Pusher `membership.changed`.
	const { data: memberships } = usePropertyMemberships(propertyId)
	const authorsById = useMemo(() => {
		const m = new Map<
			string,
			{ user: UserSummaryDTO; role: ChatRole; title: string }
		>()
		if (memberships?.owner) {
			const u = memberships.owner
			m.set(u.id, {
				user: u,
				role: "owner",
				title: `${getDisplayName(u)} (${tAll.properties.share.members.ownerLabel})`,
			})
		}
		for (const mem of memberships?.memberships ?? []) {
			m.set(mem.user.id, {
				user: mem.user,
				role: mem.role,
				title: `${getDisplayName(mem.user)} (${tAll.properties.share.roles[mem.role]})`,
			})
		}
		return m
	}, [memberships, tAll])

	const { state, sendMessage, loadOlder, hasOlder, loadingOlder } = useChat(
		propertyId,
		chatId,
		projectId,
	)
	const topSentinelRef = useRef<HTMLDivElement | null>(null)
	useTopVisibleTrigger({
		sentinelRef: topSentinelRef,
		enabled: state.status === "ready" && hasOlder,
		rearmKey: state.status === "ready" ? state.messages.length : 0,
		onVisible: loadOlder,
	})
	const { acknowledge } = useAcknowledgeChat(propertyId, chatId, projectId)

	// "Mareczek is working" indicator for THIS chat thread (ZPL-152). The
	// channel hooks (mounted by the parent page) write the flag from realtime
	// events; here it's also set optimistically when a send mentions Mareczek.
	const queryClient = useQueryClient()
	const thinking = useMareczekThinking(chatId)

	// Safety net: if both the reply and the `thinking-cleared` event go
	// missing, don't let the indicator spin forever.
	useEffect(() => {
		if (!thinking.active || !chatId) return
		const id = window.setTimeout(
			() => setMareczekThinking(queryClient, chatId, false),
			90_000,
		)
		return () => window.clearTimeout(id)
	}, [thinking, chatId, queryClient])

	// Register this chat as "active" while the rail is open so app-wide mention
	// toasts (ZPL-147) don't fire for the chat the user is already viewing.
	const { setActiveChatId } = useActiveChat()
	useEffect(() => {
		if (!isOpen || !chatId) return
		setActiveChatId(chatId)
		return () => setActiveChatId((cur) => (cur === chatId ? null : cur))
	}, [isOpen, chatId, setActiveChatId])

	const bodyRef = useRef<HTMLDivElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const [draft, setDraft] = useState("")
	const [sending, setSending] = useState(false)
	const [sendError, setSendError] = useState<string | null>(null)

	// @-mention candidates scoped to the chat: project members for a project
	// chat (the `members` prop), else the property memberships loaded above.
	// Mareczek is added by the picker itself and pinned on top.
	const mentionPeople = useMemo<MentionCandidate[]>(() => {
		// The property owner participates in every shared chat (incl. project
		// chats, where they aren't in `members`), so include them in both scopes.
		const owner = memberships?.owner ? [memberships.owner] : []
		const source: UserSummaryDTO[] = projectId
			? [...owner, ...(members ?? [])]
			: [...owner, ...(memberships?.memberships?.map((m) => m.user) ?? [])]
		const seen = new Set<string>()
		return source
			.filter((u) => {
				if (u.id === myUserId || seen.has(u.id)) return false
				seen.add(u.id)
				return true
			})
			.map((u) => ({
				key: u.id,
				kind: "user" as const,
				userId: u.id,
				displayName: getDisplayName(u),
				handle: mentionHandle(getDisplayName(u)),
				email: u.email,
			}))
	}, [projectId, members, memberships, myUserId])

	const mention = useMentionCompose({
		people: mentionPeople,
		draft,
		setDraft,
		textareaRef,
	})
	const mentionListId = `shared-chat-mentions-${chatId ?? "pending"}`

	// Resolve a mention's CURRENT handle from its userId (so a later rename is
	// reflected in old messages); null falls back to the stored snapshot.
	const resolveMentionHandle = useCallback(
		(userId: string): string | null => {
			const u = authorsById.get(userId)?.user
			return u ? mentionHandle(getDisplayName(u)) : null
		},
		[authorsById],
	)

	const newest = state.status === "ready" ? (state.messages[0] ?? null) : null
	const newestId = newest?.id ?? null
	const newestActorIsSelf =
		newest?.actorType === "user" && newest?.actorId === myUserId
	useBottomVisibleTrigger({
		scrollRef: bodyRef,
		enabled:
			isOpen &&
			state.status === "ready" &&
			newestId !== null &&
			chatId !== undefined,
		onSettled: () => {
			if (newestId) acknowledge(newestId)
		},
	})
	// Auto-ack when a new (non-self) message arrives while the rail is open
	// and the window has focus — `isOpen` replaces the older
	// IntersectionObserver gate now that the rail isn't inline.
	useAutoAckOnArrival({
		enabled: isOpen && chatId !== undefined,
		newestMessageId: newestId,
		newestActorIsSelf,
		onAck: acknowledge,
	})

	// Snap to the bottom only when the NEWEST message changes (or on open) —
	// loading an older page must not yank the user back down.
	const snapNewestId =
		state.status === "ready" ? (state.messages[0]?.id ?? null) : null
	useEffect(() => {
		if (!isOpen || !snapNewestId) return
		const el = bodyRef.current
		if (!el) return
		el.scrollTop = el.scrollHeight
	}, [snapNewestId, isOpen])

	// biome-ignore lint/correctness/useExhaustiveDependencies: draft change must re-fire so the textarea re-measures scrollHeight; the function body reads no React state directly.
	useEffect(() => {
		const el = textareaRef.current
		if (!el) return
		el.style.height = "auto"
		el.style.height = `${Math.min(el.scrollHeight, 200)}px`
	}, [draft])

	useEffect(() => {
		if (!isOpen) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [isOpen, onClose])

	// Land the cursor in the composer each time the rail opens. The rail is
	// always mounted but `inert` while closed; React effects run after the
	// inert attribute is removed, so .focus() succeeds.
	useEffect(() => {
		if (!isOpen) return
		textareaRef.current?.focus()
	}, [isOpen])

	// React's TS types define `inert` as boolean, while React DOM warns when a
	// boolean is passed. Manage the present/absent string attribute directly.
	useLayoutEffect(() => {
		applyChatRailInert(railRef.current, isOpen)
	}, [isOpen])

	// Re-focus the composer when a send completes, matching the Mareczek
	// panel's behaviour (so the user can keep typing without re-clicking).
	const wasSendingRef = useRef(sending)
	useEffect(() => {
		if (wasSendingRef.current && !sending) {
			textareaRef.current?.focus()
		}
		wasSendingRef.current = sending
	}, [sending])

	const submit = async (e?: FormEvent) => {
		e?.preventDefault()
		const content = draft.trim()
		if (!content || sending) return
		const mentions = mention.resolveMentions(content)
		setSending(true)
		setSendError(null)
		// Mentioning Mareczek always yields a reply — show the indicator from
		// the send, before the API's first `mareczek.thinking` event arrives.
		const expectsMareczek = Boolean(mentions?.mareczek && chatId)
		if (expectsMareczek && chatId) {
			setMareczekThinking(queryClient, chatId, true)
		}
		try {
			await sendMessage(content, mentions)
			setDraft("")
			mention.reset()
			if (textareaRef.current) textareaRef.current.style.height = "auto"
		} catch (err) {
			if (expectsMareczek && chatId) {
				setMareczekThinking(queryClient, chatId, false)
			}
			setSendError(err instanceof Error ? err.message : "")
		} finally {
			setSending(false)
		}
	}

	return (
		<aside
			ref={railRef}
			aria-label={t.headerName}
			aria-hidden={!isOpen}
			className={cn(
				"fixed right-0 bottom-0 z-30 w-full md:w-[360px] bg-background border-l border-border flex flex-col",
				// Mobile: sit below the role header (h-7) + property top bar (h-12) = 76px.
				// Mobile clears the fixed mobile chrome; desktop is full window
				// height (the md:top-12 offset died with the desktop top bar).
				"top-[76px] md:top-0",
				"transition-transform duration-200 ease-out",
				isOpen ? "translate-x-0" : "translate-x-full",
				"shadow-2xl",
			)}
		>
			<header className="flex items-center justify-between px-4 py-3 border-b border-border">
				<h2 className="text-base font-bold text-foreground">{t.headerName}</h2>
				<button
					type="button"
					onClick={onClose}
					aria-label={t.closeLabel}
					className="text-muted-foreground hover:text-foreground hover:bg-accent transition-colors cursor-pointer h-7 w-7 flex items-center justify-center rounded-md"
				>
					<CloseIcon />
				</button>
			</header>
			<div
				ref={bodyRef}
				className={cn(
					"flex-1 overflow-y-auto overflow-x-hidden p-4 flex flex-col-reverse gap-2 text-sm",
					CHAT_SCROLL_AREA_CLASS,
				)}
			>
				{/* flex-col-reverse: first child renders at the bottom, under the
				    newest message — where the pending reply will appear. */}
				{thinking.active && (
					<ThinkingBubble
						label={tAll.chat.thinkingLabel}
						text={thinking.text}
					/>
				)}
				{state.status === "loading" && (
					<div className="text-muted-foreground/80 text-xs text-center m-auto">
						…
					</div>
				)}
				{state.status === "error" && (
					<div className="text-destructive text-xs text-center m-auto">
						{state.message}
					</div>
				)}
				{state.status === "ready" && state.messages.length === 0 && (
					<div className="text-muted-foreground/80 text-xs text-center m-auto">
						{t.emptyState}
					</div>
				)}
				{state.status === "ready" &&
					state.messages.map((m) => {
						const mine = m.actorType === "user" && m.actorId === myUserId
						const isAi = m.actorType === "ai"
						const authorEntry = isAi ? null : authorsById.get(m.actorId)
						const role: ChatRole = isAi ? "ai" : (authorEntry?.role ?? "member")
						const title = resolveSharedChatAuthorTitle({
							actorType: m.actorType,
							authorTitle: authorEntry?.title ?? null,
							deletedUserLabel: t.deletedUser,
							aiAuthorLabel: "Mareczek",
						})
						const user = authorEntry?.user ?? null
						const shortTime = formatShortTime(m.createdAt, locale)
						const fullTime = formatFullDateTime(m.createdAt, locale)
						// Mareczek's mutation-approval card (ZPL-122/123) in a shared chat:
						// the mentioner approves on behalf of the chat (ZPL-149). chatId is
						// always set when a card exists — cards only post to a live thread.
						if ("agentAction" in m.content && chatId) {
							return (
								// Full-width row: the bubble's percentage max-width resolves against
								// the rail column, so the card hugs its content — a
								// fit-content (self-start) row made the percentage resolve
								// against the bubble itself and wrapped the CTAs mid-word.
								<div key={m.id} className="flex items-end gap-2">
									<AuthorAvatar user={user} chatRole={role} title={title} />
									<div className={chatBubbleClass("ai")}>
										<AgentActionCard
											action={m.content.agentAction}
											propertyId={propertyId}
											chatId={chatId}
											t={tAll}
										/>
									</div>
									<time
										dateTime={m.createdAt}
										title={fullTime}
										className="text-[11px] text-muted-foreground/80 leading-none pb-1 tabular-nums"
									>
										{shortTime}
									</time>
								</div>
							)
						}
						const text = renderChatMessage(m.content, {
							t: tAll,
							resolveUser: (id) => authorsById.get(id)?.user ?? null,
						})
						// Mareczek's prose replies are markdown — render them so #139
						// references become in-app links (and a legacy FAILURE marker
						// flags the bubble). Templated AI/system posts and plain user
						// text stay as-is; user mentions render via MentionText.
						const isMareczekProse = isAi && "message" in m.content
						const { status, body } = isMareczekProse
							? parseStatusMarker(text)
							: { status: null, body: text }
						const failed = status === "failure"
						let bubbleContent: ReactNode
						if (isMareczekProse) {
							bubbleContent = (
								<MessageMarkdown ownedOrigins={ownedOrigins}>
									{body}
								</MessageMarkdown>
							)
						} else if (
							"message" in m.content &&
							m.content.mentions &&
							m.content.mentions.length > 0
						) {
							bubbleContent = (
								<MentionText
									text={m.content.message}
									mentions={m.content.mentions}
									resolveHandle={resolveMentionHandle}
								/>
							)
						} else {
							bubbleContent = text
						}
						return (
							<div
								key={m.id}
								className={cn(
									"flex items-end gap-2",
									mine && "flex-row-reverse",
								)}
							>
								<AuthorAvatar user={user} chatRole={role} title={title} />
								<div
									className={chatBubbleClass(
										isAi ? "ai" : mine ? "own" : "other",
										{ failed },
									)}
								>
									{bubbleContent}
								</div>
								<time
									dateTime={m.createdAt}
									title={fullTime}
									className="text-[11px] text-muted-foreground/80 leading-none pb-1 tabular-nums"
								>
									{shortTime}
								</time>
							</div>
						)
					})}
				{state.status === "ready" && loadingOlder && (
					<div className="text-muted-foreground text-xs text-center py-1">
						…
					</div>
				)}
				{state.status === "ready" && hasOlder && (
					<div ref={topSentinelRef} className="h-px shrink-0" />
				)}
			</div>
			{sendError && (
				<div className="text-xs text-destructive px-4 py-1 border-t border-border">
					{sendError}
				</div>
			)}
			<form onSubmit={submit} className="flex gap-2 p-3 border-t border-border">
				<div
					role="combobox"
					aria-label={t.composerPlaceholder}
					aria-haspopup="listbox"
					aria-expanded={mention.open}
					aria-controls={mention.open ? mentionListId : undefined}
					aria-activedescendant={
						mention.open
							? `${mentionListId}-option-${mention.activeIndex}`
							: undefined
					}
					aria-autocomplete="list"
					className="relative flex-1"
					tabIndex={0}
				>
					{mention.open ? (
						<MentionPicker
							id={mentionListId}
							label={t.mentionSuggestionsLabel}
							candidates={mention.candidates}
							activeIndex={mention.activeIndex}
							onSelect={mention.select}
							onHover={mention.setActiveIndex}
						/>
					) : null}
					<textarea
						ref={textareaRef}
						value={draft}
						onChange={mention.onChange}
						onKeyDown={(e) => {
							if (mention.onKeyDown(e)) return
							if (e.key === "Enter" && !e.shiftKey) {
								e.preventDefault()
								submit()
							}
						}}
						onKeyUp={mention.onCaretMove}
						onClick={mention.onCaretMove}
						placeholder={t.composerPlaceholder}
						disabled={sending || !chatId}
						rows={1}
						className={cn("w-full", CHAT_COMPOSER_FIELD_CLASS)}
					/>
				</div>
				<ChatSendButton
					disabled={sending || !draft.trim() || !chatId}
					label={t.sendButtonLabel}
				/>
			</form>
		</aside>
	)
}
